import { P, match } from 'ts-pattern';
import { BalustradeSectionType, PositionType, PostConnectionType, PostSharingType, PostType } from '../__generated/model/index.js';
import { getDeckSectionHandrail, getStairsSectionHandrail } from './handrail.js';
import { getDeckPositionType, getStairsPositionType } from './position-type.js';
import { getBalustradeSectionPosts, getBalustradeSectionStairPosts } from './posts.js';
const getPostConnectionType = (positionType)=>{
    return match(positionType).with(PositionType.LeftEnd, ()=>PostConnectionType.None).with(PositionType.RightEnd, ()=>PostConnectionType.None).with(PositionType.StairsBottomLeftEnd, ()=>PostConnectionType.None).with(PositionType.StairsBottomRightEnd, ()=>PostConnectionType.None).with(PositionType.StairsTopLeftEnd, ()=>PostConnectionType.None).with(PositionType.StairsTopRightEnd, ()=>PostConnectionType.None).with(PositionType.MidPost, ()=>PostConnectionType.Shared).with(PositionType.Intermediate, ()=>PostConnectionType.None).with(PositionType.ThinIntermediate, ()=>PostConnectionType.None).with(PositionType.AngledIntermediate, ()=>PostConnectionType.None).with(PositionType.FlatFlatRight, ()=>PostConnectionType.Shared).with(PositionType.FlatFlatAcuteLeft, ()=>PostConnectionType.TwoPosts).with(PositionType.FlatFlatAcuteRight, ()=>PostConnectionType.TwoPosts).with(PositionType.FlatFlatObtuse, ()=>PostConnectionType.Shared).with(PositionType.FlatStairsStraightTopLeft, ()=>PostConnectionType.Shared).with(PositionType.FlatStairsStraightTopRight, ()=>PostConnectionType.Shared).with(PositionType.FlatStairsStraightBottomLeft, ()=>PostConnectionType.Shared).with(PositionType.FlatStairsStraightBottomRight, ()=>PostConnectionType.Shared).with(PositionType.FlatStairsCornerTopRight, ()=>PostConnectionType.Shared).with(PositionType.FlatStairsCornerTopLeft, ()=>PostConnectionType.Shared).with(PositionType.FlatStairsCornerBottomRight, ()=>PostConnectionType.Shared).with(PositionType.FlatStairsCornerBottomLeft, ()=>PostConnectionType.Shared).with(PositionType.FlatStairsOtherTopLeft, ()=>PostConnectionType.TwoPosts).with(PositionType.FlatStairsOtherTopRight, ()=>PostConnectionType.TwoPosts).with(PositionType.FlatStairsOtherBottomLeft, ()=>PostConnectionType.TwoPosts).with(PositionType.FlatStairsOtherBottomRight, ()=>PostConnectionType.TwoPosts).with(PositionType.StairsFlatOtherTopLeft, ()=>PostConnectionType.TwoPosts).with(PositionType.StairsFlatOtherTopRight, ()=>PostConnectionType.TwoPosts).with(PositionType.StairsFlatOtherBottomLeft, ()=>PostConnectionType.TwoPosts).with(PositionType.StairsFlatOtherBottomRight, ()=>PostConnectionType.TwoPosts).exhaustive();
};
const getDeckBalustradeLeftX = (positionType, connection, postType, angle)=>{
    const postSize = match(postType).with(PostType.Metal, ()=>50).with(PostType.Timber, ()=>90).exhaustive();
    return match(positionType).with(PositionType.LeftEnd, ()=>{
        return 100;
    }).with(PositionType.FlatFlatAcuteLeft, ()=>{
        if (!angle) {
            throw new Error('Angle is required for FlatFlatAcuteLeft position type');
        }
        if (angle > Math.PI) {
            return 450;
        }
        return -50;
    }).with(PositionType.FlatFlatRight, ()=>{
        if (!angle) {
            throw new Error('Angle is required for FlatFlatRight position type');
        }
        const direction = angle > Math.PI ? 1 : -1;
        return 100 * direction;
    }).with(PositionType.FlatFlatObtuse, ()=>{
        if (!angle) {
            throw new Error('Angle is required for FlatFlatObtuse position type');
        }
        const remainderAngle = 2 * Math.PI - angle;
        const offset = 100 / Math.tan(remainderAngle / 2);
        return offset;
    }).with(PositionType.FlatStairsStraightTopRight, ()=>{
        return 100;
    }).with(PositionType.FlatStairsStraightBottomLeft, ()=>{
        return 100;
    }).with(PositionType.FlatStairsCornerTopRight, ()=>{
        return -100;
    }).with(PositionType.FlatStairsCornerBottomLeft, ()=>{
        return -100;
    }).with(PositionType.FlatStairsOtherTopRight, ()=>{
        return 125 + postSize - 150 * Math.sin(connection.angle);
    }).with(PositionType.FlatStairsOtherBottomLeft, ()=>{
        return 125 + postSize - 150 * Math.sin(connection.angle);
    }).with(P.union(PositionType.RightEnd, PositionType.FlatFlatAcuteRight, PositionType.FlatStairsStraightTopLeft, PositionType.FlatStairsStraightBottomRight, PositionType.FlatStairsCornerTopLeft, PositionType.FlatStairsCornerBottomRight, PositionType.FlatStairsOtherTopLeft, PositionType.FlatStairsOtherBottomRight, PositionType.MidPost, PositionType.Intermediate, PositionType.ThinIntermediate, PositionType.AngledIntermediate, PositionType.StairsBottomLeftEnd, PositionType.StairsBottomRightEnd, PositionType.StairsTopLeftEnd, PositionType.StairsTopRightEnd, PositionType.StairsFlatOtherTopLeft, PositionType.StairsFlatOtherTopRight, PositionType.StairsFlatOtherBottomLeft, PositionType.StairsFlatOtherBottomRight), ()=>{
        throw new Error(`Can not calculate balustrade left x position! ${positionType} is not a valid position for left end of the Balustrade!`);
    }).exhaustive();
};
const getDeckBalustradeRightX = (positionType, sectionLength, angle, postType)=>{
    const postSize = match(postType).with(PostType.Metal, ()=>50).with(PostType.Timber, ()=>90).exhaustive();
    return match(positionType).with(PositionType.RightEnd, ()=>{
        return sectionLength - 100;
    }).with(PositionType.FlatFlatAcuteRight, ()=>{
        if (!angle) {
            throw new Error('Angle is required for FlatFlatAcuteRight position type');
        }
        if (angle > Math.PI) {
            return sectionLength - 450;
        }
        return sectionLength + 50;
    }).with(PositionType.FlatFlatRight, ()=>{
        if (!angle) {
            throw new Error('Angle is required for FlatFlatRight position type');
        }
        const direction = angle > Math.PI ? 1 : -1;
        return sectionLength - 100 * direction;
    }).with(PositionType.FlatFlatObtuse, ()=>{
        if (!angle) {
            throw new Error('Angle is required for FlatFlatObtuse position type');
        }
        const remainderAngle = 2 * Math.PI - angle;
        const offset = 100 / Math.tan(remainderAngle / 2);
        return sectionLength - offset;
    }).with(PositionType.FlatStairsStraightTopLeft, ()=>{
        return sectionLength - 100;
    }).with(PositionType.FlatStairsStraightBottomRight, ()=>{
        return sectionLength - 100;
    }).with(PositionType.FlatStairsCornerTopLeft, ()=>{
        return sectionLength + 100;
    }).with(PositionType.FlatStairsCornerBottomRight, ()=>{
        return sectionLength + 100;
    }).with(PositionType.FlatStairsOtherTopLeft, ()=>{
        if (!angle) {
            throw new Error('Angle is required for FlatStairsOtherTopLeft position type');
        }
        return sectionLength - (150 + postSize) + 150 * Math.sin(angle);
    }).with(PositionType.FlatStairsOtherBottomRight, ()=>{
        if (!angle) {
            throw new Error('Angle is required for FlatStairsOtherBottomRight position type');
        }
        return sectionLength - (150 + postSize) + 150 * Math.sin(angle);
    }).with(P.union(PositionType.LeftEnd, PositionType.FlatFlatAcuteLeft, PositionType.FlatStairsStraightTopRight, PositionType.FlatStairsStraightBottomLeft, PositionType.FlatStairsCornerTopRight, PositionType.FlatStairsCornerBottomLeft, PositionType.FlatStairsOtherTopRight, PositionType.FlatStairsOtherBottomLeft, PositionType.MidPost, PositionType.Intermediate, PositionType.ThinIntermediate, PositionType.AngledIntermediate, PositionType.StairsBottomLeftEnd, PositionType.StairsBottomRightEnd, PositionType.StairsTopLeftEnd, PositionType.StairsTopRightEnd, PositionType.StairsFlatOtherTopLeft, PositionType.StairsFlatOtherTopRight, PositionType.StairsFlatOtherBottomLeft, PositionType.StairsFlatOtherBottomRight), ()=>{
        throw new Error(`Can not calculate balustrade right x position! ${positionType} is not a valid position for right end of the Balustrade!`);
    }).exhaustive();
};
const getStairsLength = (leftPositionType, rightPositionType, length, goingDistance)=>{
    if (rightPositionType === PositionType.StairsBottomLeftEnd) {
        return length - goingDistance / 2;
    }
    if (leftPositionType === PositionType.StairsBottomRightEnd) {
        return length - goingDistance / 2;
    }
    if ([
        PositionType.FlatStairsStraightBottomLeft,
        PositionType.FlatStairsCornerBottomLeft,
        PositionType.StairsFlatOtherBottomLeft
    ].includes(rightPositionType)) {
        return length + 100;
    }
    if ([
        PositionType.FlatStairsStraightBottomRight,
        PositionType.FlatStairsCornerBottomRight,
        PositionType.StairsFlatOtherBottomRight
    ].includes(leftPositionType)) {
        return length + 100;
    }
    return length;
};
const getStairsBalustradeLeftX = (positionType)=>{
    return match(positionType).with(PositionType.StairsBottomRightEnd, ()=>{
        return -100;
    }).with(P.union(PositionType.FlatStairsStraightBottomRight, PositionType.FlatStairsCornerBottomRight, PositionType.StairsFlatOtherBottomRight), ()=>{
        return -100;
    }).with(P.union(PositionType.StairsTopLeftEnd, PositionType.FlatStairsStraightTopLeft, PositionType.FlatStairsCornerTopLeft, PositionType.StairsFlatOtherTopLeft), ()=>{
        return -100;
    }).otherwise(()=>{
        throw new Error(`Can not calculate balustrade right x position! ${positionType} is not a valid position for right end of the Balustrade!`);
    });
};
const getStairsBalustradeRightX = (positionType, length)=>{
    return match(positionType).with(PositionType.StairsBottomLeftEnd, ()=>{
        return length;
    }).with(PositionType.StairsTopRightEnd, ()=>{
        return length;
    }).with(P.union(PositionType.FlatStairsStraightBottomLeft, PositionType.FlatStairsCornerBottomLeft, PositionType.StairsFlatOtherBottomLeft), ()=>{
        return length;
    }).with(P.union(PositionType.FlatStairsStraightTopRight, PositionType.FlatStairsCornerTopRight, PositionType.StairsFlatOtherTopRight), ()=>{
        return length;
    }).otherwise(()=>{
        throw new Error(`Can not calculate balustrade right x position! ${positionType} is not a valid position for right end of the Balustrade!`);
    });
};
const getBalustradeSections = (shape, balustrade, maxBalustradeSectionLength, maxPostDistance, useThinIntermediatePosts, goingDistance, riserDistance)=>{
    const numberOfSections = match(shape.type).with('line', ()=>{
        return Math.ceil(balustrade.length / maxBalustradeSectionLength);
    }).with('rectangle', ()=>{
        return 1;
    }).exhaustive();
    const step = balustrade.length / numberOfSections;
    const sections = Array.from({
        length: numberOfSections
    }, (_, i)=>{
        const section = match(i).with(numberOfSections - 1, ()=>{
            return BalustradeSectionType.End;
        }).otherwise(()=>{
            return BalustradeSectionType.Intermediate;
        });
        const leftPositionType = match(i).with(0, ()=>{
            return balustrade.left.positionType;
        }).otherwise(()=>{
            return PositionType.MidPost;
        });
        const rightPositionType = match(i).with(numberOfSections - 1, ()=>{
            return balustrade.right.positionType;
        }).otherwise(()=>{
            return PositionType.MidPost;
        });
        const leftConnectionType = getPostConnectionType(leftPositionType);
        const rightConnectionType = getPostConnectionType(rightPositionType);
        const left = match(i).with(0, ()=>balustrade.left.x).otherwise(()=>balustrade.left.x + i * step);
        const right = match(i).with(numberOfSections - 1, ()=>balustrade.right.x).otherwise(()=>balustrade.left.x + (i + 1) * step);
        const length = right - left;
        const postSharingType = section === BalustradeSectionType.Intermediate ? PostSharingType.Last : balustrade.postSharingType;
        return {
            key: `${balustrade.key}_section_${i}`,
            reverse: balustrade.reverse,
            edgeOffset: balustrade.edgeOffset,
            height: balustrade.height,
            length,
            slope: balustrade.slope,
            postSharingType,
            section,
            left: {
                positionType: leftPositionType,
                x: left,
                connectionType: leftConnectionType,
                angle: balustrade.left.angle,
                slope: balustrade.left.slope
            },
            right: {
                positionType: rightPositionType,
                x: right,
                connectionType: rightConnectionType,
                angle: balustrade.right.angle,
                slope: balustrade.right.slope
            }
        };
    });
    const sectionsWithPosts = sections.map((section)=>{
        const posts = match(shape.type).with('line', ()=>{
            return getBalustradeSectionPosts(section, shape, maxPostDistance, useThinIntermediatePosts);
        }).with('rectangle', ()=>{
            return getBalustradeSectionStairPosts(section, shape, maxPostDistance, goingDistance, riserDistance);
        }).exhaustive();
        return {
            ...section,
            posts
        };
    });
    return sectionsWithPosts;
};
export const getBalustrade = (shape, leftConnection, leftEntity, rightConnection, rightEntity, key, shapeLength, maxBalustradeSectionLength, maxPostDistance, useThinIntermediatePosts, goingDistance, riserDistance, reversed, edgeOffset, height, handrailType, postType)=>{
    const leftPositionType = match(shape.type).with('line', ()=>{
        return getDeckPositionType(leftEntity, leftConnection ?? undefined, 'left');
    }).with('rectangle', ()=>{
        return getStairsPositionType(leftEntity, leftConnection ?? undefined, reversed ? 'right-bottom' : 'left-top');
    }).exhaustive();
    const leftPostConnectionType = getPostConnectionType(leftPositionType);
    const rightPositionType = match(shape.type).with('line', ()=>{
        return getDeckPositionType(rightEntity, rightConnection ?? undefined, 'right');
    }).with('rectangle', ()=>{
        return getStairsPositionType(rightEntity, rightConnection ?? undefined, reversed ? 'right-top' : 'left-bottom');
    }).exhaustive();
    const rightPostConnectionType = getPostConnectionType(rightPositionType);
    const reducedShapeLength = getStairsLength(leftPositionType, rightPositionType, shapeLength, goingDistance);
    const leftX = match(shape.type).with('line', ()=>{
        return getDeckBalustradeLeftX(leftPositionType, leftConnection ?? undefined, postType, leftConnection?.angle);
    }).with('rectangle', ()=>{
        return getStairsBalustradeLeftX(leftPositionType);
    }).exhaustive();
    const rightX = match(shape.type).with('line', ()=>{
        return getDeckBalustradeRightX(rightPositionType, reducedShapeLength, rightConnection?.angle, postType);
    }).with('rectangle', ()=>{
        return getStairsBalustradeRightX(rightPositionType, reducedShapeLength);
    }).exhaustive();
    const isRightConnectedToStairs = (rightConnection?.to.shape.type === 'rectangle' || rightConnection?.from.shape.type === 'rectangle') && rightPostConnectionType === 'Shared';
    const postSharingType = isRightConnectedToStairs ? PostSharingType.Last : rightPostConnectionType === PostConnectionType.Shared ? PostSharingType.Last : PostSharingType.None;
    const leftY = match(reversed).with(false, ()=>{
        return height;
    }).with(true, ()=>{
        if (leftPostConnectionType !== PostConnectionType.None) {
            return 0;
        } else {
            return riserDistance;
        }
    }).exhaustive();
    const rightY = match(reversed).with(false, ()=>{
        if (rightPostConnectionType !== PostConnectionType.None) {
            return 0;
        } else {
            return riserDistance;
        }
    }).with(true, ()=>{
        return height;
    }).exhaustive();
    const direction = reversed ? 1 : -1;
    const isThereBottomOffset = leftY !== 0 && rightY !== 0;
    const magicalSlopeCorrection = isThereBottomOffset ? 0.01 : 0;
    const slope = Math.atan((rightY - leftY) / (rightX - leftX)) * direction + magicalSlopeCorrection;
    const length = (rightX - leftX) / Math.cos(slope);
    const leftSlope = match(shape.type).with('rectangle', ()=>{
        return slope;
    }).with('line', ()=>{
        return match(leftEntity).with(undefined, ()=>{
            return 0;
        }).with({
            type: 'Stairs'
        }, (e)=>{
            return e.$slope;
        }).with({
            type: 'DeckSection'
        }, ()=>{
            return 0;
        }).exhaustive();
    }).exhaustive();
    const rightSlope = match(shape.type).with('rectangle', ()=>{
        return slope;
    }).with('line', ()=>{
        return match(rightEntity).with(undefined, ()=>{
            return 0;
        }).with({
            type: 'Stairs'
        }, (e)=>{
            return e.$slope;
        }).with({
            type: 'DeckSection'
        }, ()=>{
            return 0;
        }).exhaustive();
    }).exhaustive();
    const balustrade = {
        key,
        reverse: reversed,
        edgeOffset,
        postSharingType,
        height,
        slope,
        left: {
            x: leftX,
            y: leftY,
            positionType: leftPositionType,
            connectionType: leftPostConnectionType,
            angle: leftConnection?.angle ?? 0,
            slope: leftSlope
        },
        right: {
            x: rightX,
            y: rightY,
            positionType: rightPositionType,
            connectionType: rightPostConnectionType,
            angle: rightConnection?.angle ?? 0,
            slope: rightSlope
        },
        length
    };
    const sections = getBalustradeSections(shape, balustrade, maxBalustradeSectionLength, maxPostDistance, useThinIntermediatePosts, goingDistance, riserDistance);
    const handrail = match(shape.type).with('line', ()=>{
        return getDeckSectionHandrail(leftPositionType, rightPositionType, leftX, rightX, rightPostConnectionType, handrailType, maxBalustradeSectionLength, balustrade.length);
    }).with('rectangle', ()=>{
        return getStairsSectionHandrail(leftPositionType, rightPositionType, leftX, rightX, height, rightPostConnectionType, handrailType, postType, sections.flatMap((s)=>s.posts), reversed, balustrade.length);
    }).exhaustive();
    return {
        ...balustrade,
        sections,
        handrail
    };
};

//# sourceMappingURL=balustrade.js.map